import { useCallback, useEffect, useRef, useState } from 'react';
import { useGenericBi } from './use-generic-bi';
import { useProductName } from './use-brand';
import { useTranslation } from './i18n';
import { usePageLoadBi } from './use-page-load-bi';
import { connectorDisplayName, fetchConnectors, type ConnectorStatus } from './connectors';
import type { ComingSoonConnector } from './connector-requests';
import { deleteMcpConnection, fetchMcpConnections, type McpConnectionSummary } from './mcp';
import { useConnectConnector } from './use-connect-connector';
import { CONNECTORS_PAGE } from './connectors-bi';
import { useConfirm } from '../components/ConfirmDialog';
import { queryClient, queryKeys } from './query-client';

// Shared connectors logic for both brand bodies (components/{orion,symphony}/ConnectorsBody).
// The data layer lives here; the connect/disconnect/add-another/poll RAIL DISPATCH lives in
// `useConnectConnector` (lib/use-connect-connector.ts) so the empty-chat suggested-task CONNECT chip
// runs the identical flow rather than a second copy. See the page CLAUDE.md rule.

// Re-exported for the ~12 components that still import these from here.
export { CONNECTORS_PAGE };
export { accountCountFor } from './use-connect-connector';

// ===== Apps (Composio + native calendar) =====

export type AppsState =
  | { kind: 'loading' }
  | { kind: 'error' }
  | {
      kind: 'ready';
      enabled: boolean;
      connectors: ConnectorStatus[];
      multiAccountEnabled: boolean;
      gmailScopesEnabled: boolean;
      comingSoon: ComingSoonConnector[];
    };

/**
 * The curated Connectors row order, ranked by how many accounts actually have each connector CONNECTED:
 * distinct metasites per toolkit in the server's `connectors-status` log, 13-27 Aug 2026 (2,990 accounts
 * opened the screen, 1,670 of them had at least one connection). A toolkit not named here follows
 * alphabetically, so whatever Composio's catalog gains tomorrow still renders - just after the measured ones.
 *
 * It replaces a hand-picked list of 10 that had drifted badly from what accounts do: Instagram (3rd by
 * accounts) and Facebook (4th) were unranked and sat in the alphabetical mush behind ActiveCampaign and
 * Airtable; Slack was ranked 9th and is 29th; and one rank slot went to `googlecalendar`, a row the server
 * drops UNCONDITIONALLY (`dropUnavailableComposioRows`) and which therefore could never appear at all.
 *
 * Two deliberate exceptions to the measured ranking, each of which would otherwise read as a bug:
 * - The three native (Nylas) `calendar-*` rows stay ADJACENT, under Google Calendar's own rank. They are one
 *   feature split by provider, and dropping Apple Calendar (9 accounts) to the end of the list is precisely
 *   the defect `ConnectorsOrderTests` was written for.
 * - `canva` sits between Google Meet and Zoom by product decision, above its measured rank. Note it is a
 *   DEPRIORITIZED slug (`isDeprioritizedConnectorSlug`): the server drops its row unless the account already
 *   connected it, so this placement is only ever visible to those accounts.
 *
 * Mirrored 1:1 on both phones (`ConnectorsScreen.swift`, `ConnectorsScreen.kt`) - keep the three identical;
 * `test/connectors-order.test.ts`, `ConnectorsOrderTests` and `ConnectorsOrderTest` pin them. Re-cutting this
 * costs an app release on the phones, which is the argument for the follow-up: a `rank` on the status DTO fed
 * by a databag list, so reordering becomes a config change on the server instead of three code edits.
 */
const CONNECTOR_ORDER = [
  'gmail',
  'calendar-google',
  'calendar-microsoft',
  'calendar-apple',
  'instagram',
  'facebook',
  'linkedin',
  // No Aug-2026 measurement behind this one: WhatsApp was not connectable then, so it is placed on the
  // evidence there is rather than above the measured rows. Unpromoted and buried in the slug-sorted
  // tail it still took 5 connects in its first 17 hours, which is why it is on the list at all.
  'whatsapp',
  'googledrive',
  'googlesheets',
  'youtube',
  'googledocs',
  'google_analytics',
  'google_search_console',
  'gsc',
  'google_maps',
  'outlook',
  'notion',
  'googlemeet',
  'canva',
  'zoom',
  'github',
  'calendly',
  'googleads',
  'googletasks',
  'excel',
  'square',
  'stripe',
  'hubspot',
  'mailchimp',
  'slack',
  'googleslides',
  'one_drive',
  'dropbox',
  'quickbooks',
  'reddit',
  'figma',
  'discord',
  'microsoft_teams',
  'airtable',
  'clickup',
  'asana',
  'pinterest',
  'cal',
  'supabase',
  'apollo',
  'todoist',
  'trello',
  'discordbot',
  'linear',
  'reddit_ads',
  'typeform',
  'shopify',
  'pipedrive',
  'active_campaign',
  'perplexityai',
  'box',
  'eventbrite',
  'share_point',
  'jira',
  'salesforce',
  'postiz_mcp',
  'intercom',
];
const orderRank = (toolkit: string) => {
  const i = CONNECTOR_ORDER.indexOf(toolkit);
  return i === -1 ? CONNECTOR_ORDER.length : i;
};
export function sortConnectors(list: ConnectorStatus[]): ConnectorStatus[] {
  return [...list].sort(
    (a, b) =>
      Number(b.connected) - Number(a.connected) ||
      orderRank(a.toolkit) - orderRank(b.toolkit) ||
      connectorDisplayName(a).localeCompare(connectorDisplayName(b)),
  );
}

/**
 * The registry slugs the apps section is actually rendering a row for. The MCP section hides exactly
 * these connections and nothing more, so a registry server is never on the screen twice and never absent
 * from both places.
 *
 * Per-slug rather than the all-or-nothing rule it replaces, because a gated connector's row disappears
 * the moment its rollout closes while the CONNECTION lives on. Under the old rule one ungated row
 * (WordPress) was enough to hide EVERY registry connection — so a user who connected TikTok during a
 * pilot and was later dropped from the allowlist lost the connector row AND the MCP row, leaving no way
 * to disconnect it on web at all. The server deliberately keeps disconnect ungated for exactly that case;
 * this is the client half of the same promise. It also still covers the original reason: on a catalog
 * read failure or an unconfigured Composio key the apps section renders no rows, so this set is empty and
 * every connection shows here.
 *
 * `null` means "not answerable yet", which is NOT the same as the empty set and must not collapse into it:
 * the two controllers fetch independently, and the MCP read routinely lands first (the apps read carries
 * Composio's 4s timeout and its 13-21s tail). Treating loading as "the apps section claims nothing" renders
 * a connected server as a raw MCP row for that whole window and then yanks it once its card arrives.
 */
export function connectorRowSlugs(state: AppsState): Set<string> | null {
  if (state.kind === 'loading') {
    return null;
  }
  if (state.kind !== 'ready') {
    return new Set();
  }
  return new Set(state.connectors.filter((connector) => connector.provider === 'mcp').map((connector) => connector.toolkit));
}

/** Whether the MCP section shows this connection: everything that isn't a registry server, plus the registry
 *  servers the apps section is not rendering a row for. Held back entirely while that answer is pending. */
export function mcpSectionShowsConnection(entry: { slug: string } | undefined, rowedSlugs: Set<string> | null): boolean {
  if (!entry) {
    return true;
  }
  return rowedSlugs !== null && !rowedSlugs.has(entry.slug);
}

export interface AppsController {
  state: AppsState;
  /** The keys with an action in flight — see `ConnectConnector.busy`. A row asks about ITSELF. */
  busy: ReadonlySet<string>;
  actionError: string | null;
  // Apple connects with an app-specific password (not OAuth) — its card expands an inline form.
  icloudOpenFor: string | null;
  appleId: string;
  applePassword: string;
  setAppleId: (v: string) => void;
  setApplePassword: (v: string) => void;
  submitICloud: () => void;
  connect: (connector: ConnectorStatus) => void;
  addAnother: (connector: ConnectorStatus) => void;
  disconnect: (connector: ConnectorStatus, name: string, accountId?: string) => void;
  /** Re-read `/api/connectors/status`. Exposed so a caller can re-sync on its own signal — the home,
   *  kept alive by DashboardHost, calls it on return since it never remounts to re-run the load. */
  refresh: () => Promise<void>;
}

export function useAppsController(): AppsController {
  const { logPageClickBi } = useGenericBi();
  const [state, setState] = useState<AppsState>({ kind: 'loading' });
  // Fire the connectors page-view (generic loadFinish, evid 5) once apps have loaded, tagged with
  // which toolkits came back and which were already connected — so the page-view captures load state.
  usePageLoadBi(CONNECTORS_PAGE, {
    enabled: state.kind === 'ready',
    trackBack: true,
    additionalFields:
      state.kind === 'ready'
        ? {
            loaded_connectors: state.connectors.map((c) => c.toolkit).join(','),
            connected_connectors: state.connectors
              .filter((c) => c.connected)
              .map((c) => c.toolkit)
              .join(','),
            connected_count: String(state.connectors.filter((c) => c.connected).length),
          }
        : undefined,
  });
  const prevConnectedRef = useRef<Set<string> | null>(null);

  // Fire `connect_<toolkit>` (action: connected) the first time a toolkit shows up connected across
  // reads — the connect-detection BI that rides every list refresh, connect flow or not.
  const applyConnectorStatus = useCallback(
    (connectors: ConnectorStatus[]) => {
      const nowConnected = new Set(connectors.filter((c) => c.connected).map((c) => c.toolkit));
      const prevConnected = prevConnectedRef.current;
      if (prevConnected) {
        nowConnected.forEach((toolkit) => {
          if (!prevConnected.has(toolkit)) {
            logPageClickBi({ ...CONNECTORS_PAGE, button_name: `connect_${toolkit}`, action: 'connected' });
          }
        });
      }
      prevConnectedRef.current = nowConnected;
    },
    [logPageClickBi],
  );

  const refresh = useCallback(async () => {
    try {
      const status = await fetchConnectors();
      const { enabled, connectors, multiAccountEnabled, gmailScopesEnabled, comingSoon } = status;
      applyConnectorStatus(connectors);
      setState({ kind: 'ready', enabled, connectors, multiAccountEnabled, gmailScopesEnabled, comingSoon });
      // Written through to the shared `queryKeys.connectors` entry. This controller predates the query layer
      // and keeps its own state, but the Outreach tab's Alfred row, the map, the routines rail and the chat
      // cards all read that entry — and a connect or disconnect on the Tools tab re-reads through THIS
      // refresh, so without the write-through they kept the old status until their own staleTime lapsed
      // (reported: Alfred still "Connected" on Outreach after a Tools-tab disconnect).
      queryClient.setQueryData(queryKeys.connectors, status);
    } catch {
      setState({ kind: 'error' });
    }
  }, [applyConnectorStatus]);

  // Both effects reach `refresh` through a ref so neither re-runs when its identity moves. Depending on
  // it directly is what made the initial load self-perpetuating: refresh → setState → render → a new
  // `refresh` (it transitively depended on a BI logger that was rebuilt every render) → the effect fires
  // again. That was ~33 reads/sec of /api/connectors/status for as long as the page stayed open, which
  // starved the voice call's WebSocket handshake. The dependency is stable again now; this makes the
  // load-on-mount structurally incapable of flooding even if some future dependency churns.
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  useEffect(() => {
    void refreshRef.current();
  }, []);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refreshRef.current();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  // The connect/disconnect rail dispatch is shared with the suggested-task chip — see
  // `useConnectConnector`. It reads the live list for the add-another baseline through `getConnectors`
  // (a ref keeps `state` fresh without churning the connect callbacks) and re-syncs via `refresh`.
  const stateRef = useRef(state);
  stateRef.current = state;
  const getConnectors = useCallback(() => (stateRef.current.kind === 'ready' ? stateRef.current.connectors : []), []);
  // Read through the same ref as the rows, so the value the connect uses is the one the last status
  // reported rather than whatever was true when the callbacks were built.
  const getGmailScopesEnabled = useCallback(() => stateRef.current.kind === 'ready' && stateRef.current.gmailScopesEnabled, []);
  const actions = useConnectConnector({ getConnectors, getGmailScopesEnabled, refresh });

  return { state, refresh, ...actions };
}

// ===== MCP servers =====

export type McpState = { kind: 'loading' } | { kind: 'error' } | { kind: 'ready'; connections: McpConnectionSummary[] };

export const MCP_CONNECT_PROMPT = 'I want to connect a new MCP server.';

export interface McpController {
  state: McpState;
  /** Connection ids with a removal in flight. A set for the same reason the apps controller's is —
   *  removing one server must not freeze every other row on the screen. */
  busy: ReadonlySet<string>;
  actionError: string | null;
  remove: (id: string, name: string) => void;
  logConnectClick: () => void;
}

export function useMcpController(): McpController {
  const productName = useProductName();
  const { t } = useTranslation();
  const confirm = useConfirm();
  const { logPageClickBi } = useGenericBi();
  const [state, setState] = useState<McpState>({ kind: 'loading' });
  const busyRef = useRef<ReadonlySet<string>>(new Set());
  const [busy, setBusy] = useState<ReadonlySet<string>>(busyRef.current);
  const [actionError, setActionError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      // Every connection, registry-backed or not. Which of them this section actually renders is a
      // render-time call (`connectorRowSlugs`) rather than a fetch-time one, so the answer
      // changing as the apps section settles never costs a second request.
      const connections = await fetchMcpConnections();
      setState({ kind: 'ready', connections });
    } catch {
      setState({ kind: 'error' });
    }
  }, []);

  // Same ref indirection as the apps controller above. This `refresh` has no dependencies today, so the
  // effects fire once either way — but the flood that broke voice calls was one added dependency away
  // from happening here too, and the mount fetch has no reason to be coupled to refresh's identity.
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  useEffect(() => {
    void refreshRef.current();
  }, []);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refreshRef.current();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  const remove = useCallback(
    async (id: string, name: string) => {
      if (busyRef.current.has(id)) return;
      logPageClickBi({ ...CONNECTORS_PAGE, button_name: 'disconnect_mcp' });
      const confirmed = await confirm({
        message: t('connectors.disconnect.confirm', { name, productName }),
        confirmLabel: t('common.disconnect'),
        destructive: true,
        bi: { ...CONNECTORS_PAGE, modal_name: 'disconnect_mcp' },
      });
      if (!confirmed || busyRef.current.has(id)) return;
      busyRef.current = new Set(busyRef.current).add(id);
      setBusy(busyRef.current);
      setActionError(null);
      try {
        await deleteMcpConnection(id);
        await refresh();
      } catch {
        setActionError(`Couldn't remove ${name}. Please try again.`);
      } finally {
        const next = new Set(busyRef.current);
        next.delete(id);
        busyRef.current = next;
        setBusy(next);
      }
    },
    [logPageClickBi, refresh, confirm, t, productName],
  );

  const logConnectClick = useCallback(() => logPageClickBi({ ...CONNECTORS_PAGE, button_name: 'connect_mcp' }), [logPageClickBi]);

  return { state, busy, actionError, remove, logConnectClick };
}
